import {
  InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {TAB_QUERY_KEY_MAP} from '../api/tab/constant';
import {getTabDayOfWeek} from '../api/tab/getTabDayOfWeek';
import {getTabListDayOfWeek} from '../api/tab/getTabListDayOfWeek';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createDayOfWeekLayout} from '../graphql/factory/DayOfWeekLayout';
import {createDayOfWeekList} from '../graphql/factory/DayOfWeekList';
import type {
  DayOfWeekLayout,
  DayOfWeekLayoutQueryVariables,
  DayOfWeekList,
  DayOfWeekListQueryVariables,
} from '../graphql/generated/types';

/**
 * Tab 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const tabFactory = {
  /**
   * 요일연재 Layout 조회
   *
   * ⚠️ Resolver 로직:
   * - dayUid를 isNil 체크 후 undefined 또는 Number로 변환
   * - response.result 없으면 에러 throw
   * - createDayOfWeekLayout factory로 응답 변환
   */
  dayOfWeekLayout: (
    variables?: DayOfWeekLayoutQueryVariables,
    options?: Omit<
      UseQueryOptions<
        DayOfWeekLayout | null,
        PagewebError,
        DayOfWeekLayout | null
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TAB_QUERY_KEY_MAP.dayOfWeekLayout(variables),
      queryFn: async (): Promise<DayOfWeekLayout | null> => {
        const dayUid = isNil(variables?.dayUid)
          ? undefined
          : Number(variables.dayUid);

        const response = await getTabDayOfWeek(dayUid, fetcherOptions);

        if (!response.result) {
          throw createSimpleApiResponseError('요일연재 Layout 을 찾을 수 없음');
        }

        // Adapter의 combine 로직을 여기서 수행
        return createDayOfWeekLayout(response.result);
      },
      ...options,
    }),

  /**
   * 요일연재 List 조회
   *
   * ⚠️ Resolver 로직:
   * - referenceKey는 필수, 나머지는 ?? undefined 처리
   * - response.result 없으면 에러 throw
   * - createDayOfWeekList factory로 응답 변환
   */
  dayOfWeekList: (
    variables: DayOfWeekListQueryVariables,
    options?: Omit<
      UseQueryOptions<DayOfWeekList | null, PagewebError, DayOfWeekList | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TAB_QUERY_KEY_MAP.dayOfWeekList(variables),
      queryFn: async (): Promise<DayOfWeekList | null> => {
        const response = await getTabListDayOfWeek(
          {
            referenceKey: variables.referenceKey,
            dayUid: variables.dayUid ?? undefined,
            hasBanner: variables.hasBanner ?? undefined,
            sortType: variables.sortType ?? undefined,
            page: variables.page ?? undefined,
            size: variables.size ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('요일연재 List 를 찾을 수 없음');
        }

        // Adapter의 combine 로직을 여기서 수행
        return createDayOfWeekList(
          response.result,
          variables?.sortType ?? undefined
        );
      },
      ...options,
    }),

  /**
   * 요일연재 List 조회 (Infinite)
   *
   * ⚠️ Resolver 로직:
   * - referenceKey는 필수, 나머지는 ?? undefined 처리
   * - response.result 없으면 에러 throw
   * - createDayOfWeekList factory로 변환 (각 페이지마다 적용)
   */
  infiniteDayOfWeekList: (
    variables: DayOfWeekListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        DayOfWeekList,
        PagewebError,
        InfiniteData<DayOfWeekList, number>,
        DayOfWeekList,
        ReturnType<typeof TAB_QUERY_KEY_MAP.infiniteDayOfWeekList>,
        number
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: TAB_QUERY_KEY_MAP.infiniteDayOfWeekList(variables),
      queryFn: async ({pageParam}) => {
        const response = await getTabListDayOfWeek(
          {
            referenceKey: variables.referenceKey,
            dayUid: variables.dayUid ?? undefined,
            hasBanner: variables.hasBanner ?? undefined,
            sortType: variables.sortType ?? undefined,
            page: pageParam,
            size: variables.size ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('요일연재 List 를 찾을 수 없음');
        }

        return createDayOfWeekList(
          response.result,
          variables?.sortType ?? undefined
        );
      },
      ...options,
    }),
} as const;
